// ===============================
// DesiderataPlanningPortRoyal.jsx (v3)
// ===============================
// Interface jour par jour pour les désidératas du planning Port-Royal
// Le médecin clique sur un jour → popup avec choix de statut
// Si "Disponible" → ajustement des activités
// Affichage des médecins de garde sur chaque jour
// ===============================

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useAuth } from "./contexts/AuthContext.jsx";
import LogoutButton from "./components/LogoutButton.jsx";
import { getFrenchHolidayName, isFrenchPublicHoliday } from "./frenchHolidays.mjs";
import {
  PLANNING_PR_ACTIVITES_ENDPOINT,
  PLANNING_PR_DATA_ENDPOINT,
  PLANNING_PR_DESIDERATA_ENDPOINT,
  fetchPlanningPrConfig,
  planningPrDesiderataPayloadToOwn,
} from "./apiPlanningPr.js";
import { notify } from "./ui/dialogService.js";

const JOURS_SEMAINE = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const PAGE_UI_SCALE = 1.35;

const TYPES_EXCLUS = new Set(["repos", "conge"]);
const IDS_EXCLUS = new Set([
  "cs-uro-interne", "interne-1-13952", "interne-2-13953", "interne-etages",
  "iade-12-13954", "iade-10-13949",
  "garde-interne-1", "garde-interne-2", "garde-iade",
  "garde-autre", "enseignement-1", "inserm", "cf-1", "ij", "aig-1",
  "internes-des", "repos-securite", "rs-internes", "ca-rtt", "ca-rtt-internes",
  "bloc-uca-ortho", "bloc-uca-1", "cs-uca", "oph",
  "consultation-14",
]);

const LINKED_GROUPS = [
  ["bloc-2-gyneco", "bloc-3-gyneco", "bloc-1-robot-gyn"],
  ["bloc-4-uro", "bloc-5-uro", "bloc-1-robot-uro"],
  ["bloc-7-urgences", "bloc-8-pma"],
];

const LINKED_MAP = {};
LINKED_GROUPS.forEach(group => {
  group.forEach(actId => {
    LINKED_MAP[actId] = group.filter(id => id !== actId);
  });
});
function getLinkedIds(actId) { return LINKED_MAP[actId] || []; }

const STATUTS = [
  { id: "disponible", label: "Disponible", short: "Dispo", icon: "●", color: "#059669", bg: "#d1fae5", bgLight: "#ecfdf5" },
  { id: "indisponible", label: "Indisponible", short: "Indispo", icon: "●", color: "#dc2626", bg: "#fecaca", bgLight: "#fef2f2" },
  { id: "ca-rtt", label: "CA / RTT", short: "CA/RTT", icon: "●", color: "#d97706", bg: "#fef3c7", bgLight: "#fffbeb" },
  { id: "cf", label: "Congé Formation", short: "CF", icon: "●", color: "#7c3aed", bg: "#ede9fe", bgLight: "#f5f3ff" },
  { id: "enseignement", label: "Enseignement", short: "Enseign.", icon: "●", color: "#2563eb", bg: "#dbeafe", bgLight: "#eff6ff" },
  { id: "inserm", label: "INSERM", short: "INSERM", icon: "●", color: "#0891b2", bg: "#cffafe", bgLight: "#ecfeff" },
  { id: "autre", label: "Autre", short: "Autre", icon: "●", color: "#6b7280", bg: "#f3f4f6", bgLight: "#f9fafb" },
];
const STATUT_MAP = {};
STATUTS.forEach(s => { STATUT_MAP[s.id] = s; });

// ====== Utilitaires dates ======
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isFerie = (dateStr) => isFrenchPublicHoliday(dateStr);

function getCalendarWeeks(year, monthIdx) {
  const numDays = new Date(year, monthIdx + 1, 0).getDate();
  const firstDow = (new Date(year, monthIdx, 1).getDay() + 6) % 7;
  const weeks = [];
  let week = new Array(firstDow).fill(null);
  for (let d = 1; d <= numDays; d++) {
    week.push(new Date(year, monthIdx, d));
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

function getDaysInMonth(year, monthIdx) {
  const days = [];
  const n = new Date(year, monthIdx + 1, 0).getDate();
  for (let d = 1; d <= n; d++) days.push(new Date(year, monthIdx, d));
  return days;
}

function filterActivitesMARs(activites) {
  const filtered = activites.filter(a => {
    if (a.type === "separator") return true;
    if (TYPES_EXCLUS.has(a.type)) return false;
    if (IDS_EXCLUS.has(a.id)) return false;
    return true;
  });
  const result = [];
  for (const cur of filtered) {
    if (cur.type === "separator") {
      if (result.length === 0 || result[result.length - 1]?.type === "separator") continue;
    }
    result.push(cur);
  }
  while (result.length > 0 && result[result.length - 1]?.type === "separator") result.pop();
  return result;
}

function moisLabel(moisCle) {
  if (!moisCle || !moisCle.includes("-")) return "";
  const [y, m] = moisCle.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  let s = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDateLong(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  let s = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatCode(code) {
  if (!code) return "";
  const c = code.trim();
  if (c.includes(".")) {
    const parts = c.split(".");
    return `${parts[0].toUpperCase()}.${(parts[1] || "").toUpperCase()}`;
  }
  if (c.includes("_")) {
    const idx = c.indexOf("_");
    const nom = c.substring(0, idx).trim();
    const prenom = c.substring(idx + 1).trim();
    if (prenom) return `${prenom.charAt(0).toUpperCase()}.${nom.toUpperCase()}`;
    return nom.toUpperCase();
  }
  const parts = c.split(" ");
  if (parts.length >= 2) {
    const prenom = parts[parts.length - 1];
    const nom = parts.slice(0, -1).join(" ");
    return `${prenom.charAt(0).toUpperCase()}.${nom.toUpperCase()}`;
  }
  return c.toUpperCase();
}

// ====== COMPOSANT PRINCIPAL ======
const DesiderataPlanningPortRoyal = () => {
  const { user } = useAuth();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingOphPlanning, setOpeningOphPlanning] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [activitesRaw, setActivitesRaw] = useState([]);
  const [activitesLoaded, setActivitesLoaded] = useState(false);
  const [planningData, setPlanningData] = useState(null);
  const [selectedMois, setSelectedMois] = useState("");
  const [showModal, setShowModal] = useState(null);
  const [showExclusionsReminder, setShowExclusionsReminder] = useState(false);
  const [exclusionsPromptShown, setExclusionsPromptShown] = useState(false);
  const [showGuidelinesPopup, setShowGuidelinesPopup] = useState(false);
  const [guidelinesShown, setGuidelinesShown] = useState(false);

  // Étape initiale : exclusions globales d'activités
  const [globalExclusions, setGlobalExclusions] = useState({});
  const [setupDone, setSetupDone] = useState(false);
  const [lastAppliedExclusions, setLastAppliedExclusions] = useState({});

  // Données jour par jour : { "2026-04-01": { am: { statut, activites, detail }, pm: { statut, activites, detail } } }
  const [joursData, setJoursData] = useState({});

  // Popup jour - deux demi-journées
  const [selectedDay, setSelectedDay] = useState(null);
  const [popupAM, setPopupAM] = useState({ statut: null, activites: {}, detail: "", subPopup: null });
  const [popupPM, setPopupPM] = useState({ statut: null, activites: {}, detail: "", subPopup: null });
  const [halfDayWarning, setHalfDayWarning] = useState(null);

  // Jours d'activités supplémentaires
  const [joursSupplementaires, setJoursSupplementaires] = useState(0);
  const [showJoursSupPopup, setShowJoursSupPopup] = useState(false);

  /** Dernière version connue comme enregistrée (GET réussi ou POST réussi). */
  const [baselineSnapshot, setBaselineSnapshot] = useState("");
  const [desiderataHydrated, setDesiderataHydrated] = useState(false);
  const [unsavedPrompt, setUnsavedPrompt] = useState(null);
  /** Après une sauvegarde réussie : navigation portail ou changement de mois. */
  const pendingAfterSaveRef = useRef(null);

  const buildDataSnapshot = useCallback((j, ge, js) => JSON.stringify({
    joursData: j || {},
    globalExclusions: ge || {},
    joursSupplementaires: js ?? 0,
  }), []);

  const currentDataSnapshot = useMemo(
    () => buildDataSnapshot(joursData, globalExclusions, joursSupplementaires),
    [buildDataSnapshot, joursData, globalExclusions, joursSupplementaires]
  );

  const isDirty = desiderataHydrated && baselineSnapshot !== "" && currentDataSnapshot !== baselineSnapshot;

  const runPendingAfterSaveIfAny = useCallback(() => {
    const p = pendingAfterSaveRef.current;
    pendingAfterSaveRef.current = null;
    setUnsavedPrompt(null);
    if (!p) return;
    if (p.kind === "portail") {
      window.location.href = "/portail-medecin";
    } else if (p.kind === "mois" && p.mois) {
      setSelectedMois(p.mois);
    }
  }, []);

  const clearPendingAfterSave = useCallback(() => {
    pendingAfterSaveRef.current = null;
  }, []);

  const tryGoPortail = useCallback(() => {
    if (!desiderataHydrated || !isDirty) {
      window.location.href = "/portail-medecin";
      return;
    }
    setUnsavedPrompt({ mode: "portail" });
  }, [desiderataHydrated, isDirty]);

  const trySelectMois = useCallback((nextMois) => {
    if (String(nextMois) === String(selectedMois)) return;
    if (!desiderataHydrated || !isDirty) {
      setSelectedMois(String(nextMois || ""));
      return;
    }
    setUnsavedPrompt({ mode: "mois", nextMois: String(nextMois || "") });
  }, [desiderataHydrated, isDirty, selectedMois]);

  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const ACTIVITES = useMemo(() => filterActivitesMARs(activitesRaw), [activitesRaw]);
  const ACTIVITES_VISIBLES = useMemo(() => ACTIVITES.filter(a => a.type !== "separator"), [ACTIVITES]);

  const normalize = (str) => String(str || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const getRestrictionForMonth = useCallback((moisCle) => {
    const byMonth = cfg?.restrictions_par_mois;
    if (moisCle && byMonth && typeof byMonth === "object" && byMonth[moisCle]) {
      const rule = byMonth[moisCle];
      return {
        acces_restreint: rule?.acces_restreint === true,
        medecins_autorises: Array.isArray(rule?.medecins_autorises) ? rule.medecins_autorises : [],
      };
    }
    return {
      acces_restreint: cfg?.acces_restreint === true,
      medecins_autorises: Array.isArray(cfg?.medecins_autorises) ? cfg.medecins_autorises : [],
    };
  }, [cfg]);

  const currentRestriction = useMemo(
    () => getRestrictionForMonth(selectedMois || ""),
    [getRestrictionForMonth, selectedMois]
  );

  const isAccessRestreint = currentRestriction.acces_restreint === true;
  const isAutorise = useMemo(() => {
    if (!isAccessRestreint) return true;
    if (!user?.nom || !user?.prenom) return false;
    const autorises = currentRestriction.medecins_autorises || [];
    if (autorises.length === 0) return true;
    return autorises.some(a => normalize(a.nom) === normalize(user.nom) && normalize(a.prenom) === normalize(user.prenom));
  }, [isAccessRestreint, currentRestriction, user]);

  /** Raccourci vers l’édition planning OPH (multisecteurs) — Dr Wissal·e Ouechani uniquement. */
  const showOphPlanningShortcutCard = useMemo(() => {
    if (!user) return false;
    const n = normalize(user.nom || user.medecin_nom || "");
    const p = normalize(user.prenom || user.medecin_prenom || "");
    const u = normalize(user.username || "").replace(/\./g, "");
    const isOuechani = n.includes("ouechani") || u.includes("ouechani");
    const isWissal = /^wissal/.test(p) || p === "wissale";
    const isOuechaniLogin = u === "ouechaniw";
    return isOuechani && (isWissal || isOuechaniLogin);
  }, [user]);

  const openOphPlanningMultisecteur = useCallback(async () => {
    let navWindow = window;
    const popup = window.open("", "_blank");
    if (!popup) {
      notify("Le navigateur a bloqué l'ouverture de l'onglet. Autorisez les pop-ups pour ce site.");
      return;
    }
    popup.document.write("<p style='font-family:sans-serif;padding:24px'>Connexion en cours...</p>");
    navWindow = popup;
    try {
      setOpeningOphPlanning(true);
      const res = await fetch("/api/v2/multisecteur-autologin?secteur=oph", { credentials: "include" });
      const rawBody = await res.text().catch(() => "");
      if (!res.ok) {
        let detail = rawBody;
        try {
          const j = JSON.parse(rawBody);
          detail = j.detail || j.error || j.message || rawBody;
        } catch {
          /* ignore */
        }
        console.error("multisecteur-autologin HTTP", res.status, detail);
        throw new Error(String(detail || `Erreur ${res.status}`).slice(0, 240));
      }
      const data = JSON.parse(rawBody);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.user));
      navWindow.location.href =
        "/multisecteur/responsable/planning-oph-edition.html?secteur=sect-oph";
    } catch (err) {
      console.error("Erreur auto-login planning OPH multisecteurs:", err);
      const q = new URLSearchParams({ from: "port-royal-autologin", reason: "failed" });
      const hint = String(err?.message || "").trim();
      if (hint) q.set("hint", hint.slice(0, 240));
      navWindow.location.href = `/multisecteur/index.html?${q.toString()}`;
    } finally {
      setOpeningOphPlanning(false);
    }
  }, []);

  const moisOuverts = useMemo(() => {
    if (!cfg) return [];
    if (Array.isArray(cfg?.mois_ouverts)) {
      return cfg.mois_ouverts.map(m => ({ mois_cle: m.mois_cle, mois_label: m.mois_label || moisLabel(m.mois_cle) }));
    } else if (cfg?.mois_cle) {
      return [{ mois_cle: cfg.mois_cle, mois_label: cfg.mois_label || moisLabel(cfg.mois_cle) }];
    }
    return [];
  }, [cfg]);

  /** Phrase décrivant les mois ouverts à la saisie (pop-up consignes). */
  const guidelinesMoisPhrase = useMemo(() => {
    if (!moisOuverts.length) {
      return "pour le mois affiché dans le sélecteur.";
    }
    const labels = moisOuverts.map((m) => m.mois_label || moisLabel(m.mois_cle)).filter(Boolean);
    if (labels.length === 1) {
      return `pour le mois de ${labels[0]}.`;
    }
    if (labels.length === 2) {
      return `pour les mois de ${labels[0]} et ${labels[1]}.`;
    }
    return `pour les mois de ${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}.`;
  }, [moisOuverts]);

  const MOIS_CLE = selectedMois || "";
  const validMois = MOIS_CLE && MOIS_CLE.includes("-");
  const [yStr, mStr] = validMois ? MOIS_CLE.split("-") : ["2026", "04"];
  const YEAR = parseInt(yStr, 10);
  const MONTH_INDEX = parseInt(mStr, 10) - 1;

  const calendarWeeks = useMemo(() => getCalendarWeeks(YEAR, MONTH_INDEX), [YEAR, MONTH_INDEX]);
  const allDays = useMemo(() => getDaysInMonth(YEAR, MONTH_INDEX), [YEAR, MONTH_INDEX]);
  const workDays = useMemo(() => allDays.filter(d => {
    const iso = toISO(d);
    const dow = d.getDay();
    return dow !== 0 && dow !== 6 && !isFerie(iso);
  }), [allDays]);

  // ====== Chargement ======
  useEffect(() => {
    fetchPlanningPrConfig()
      .then(json => setCfg(json))
      .catch(() => setCfg(null))
      .finally(() => setCfgLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMois && moisOuverts.length > 0) setSelectedMois(moisOuverts[0].mois_cle);
  }, [moisOuverts, selectedMois]);

  useEffect(() => {
    fetch(PLANNING_PR_ACTIVITES_ENDPOINT, { credentials: "include" })
      .then(r => r.ok ? r.json() : { ok: false })
      .then(json => { if (json.ok && json.activites?.length > 0) setActivitesRaw(json.activites); })
      .catch(() => {})
      .finally(() => setActivitesLoaded(true));
  }, []);

  useEffect(() => {
    if (!MOIS_CLE) return;
    setLoading(true);
    fetch(`${PLANNING_PR_DATA_ENDPOINT}/${MOIS_CLE}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          if (r.status === 403) {
            setAccessDenied(true);
            notify("Accès non autorisé au planning Port-Royal pour ce compte.");
            window.location.href = "/portail-medecin";
          }
          return null;
        }
        return r.json();
      })
      .then(data => setPlanningData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [MOIS_CLE]);

  useEffect(() => {
    if (!MOIS_CLE || !user?.nom || !user?.prenom) return;
    setDesiderataHydrated(false);
    fetch(`${PLANNING_PR_DESIDERATA_ENDPOINT}/${MOIS_CLE}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) {
          if (r.status === 403) {
            setAccessDenied(true);
            notify("Accès non autorisé au planning Port-Royal pour ce compte.");
            window.location.href = "/portail-medecin";
          }
          return { data: null };
        }
        return r.json();
      })
      .then(json => {
        const own = planningPrDesiderataPayloadToOwn(json);
        const v = own?.valeurs;
        let nextJours = {};
        let nextGlob = {};
        let nextJs = 0;
        if (v?.version === 3) {
          nextJours = v.jours || {};
          nextGlob = v.globalExclusions || {};
          setJoursData(nextJours);
          setGlobalExclusions(nextGlob);
          setLastAppliedExclusions(nextGlob);
          // Toujours repasser par l'étape 1 à chaque ouverture
          // pour permettre la revue/modification rapide des exclusions.
          setSetupDone(false);
        } else {
          setJoursData({});
          setGlobalExclusions({});
          setLastAppliedExclusions({});
          setSetupDone(false);
        }
        if (own?.joursSupplementaires !== undefined) {
          nextJs = own.joursSupplementaires;
          setJoursSupplementaires(nextJs);
        } else {
          setJoursSupplementaires(0);
        }
        setBaselineSnapshot(JSON.stringify({
          joursData: nextJours,
          globalExclusions: nextGlob,
          joursSupplementaires: nextJs,
        }));
        setDesiderataHydrated(true);
      })
      .catch(() => {});
  }, [MOIS_CLE, user]);

  useEffect(() => {
    setExclusionsPromptShown(false);
    setShowExclusionsReminder(false);
    setGuidelinesShown(false);
    setShowGuidelinesPopup(false);
  }, [MOIS_CLE]);

  useEffect(() => {
    if (!setupDone || loading || exclusionsPromptShown) return;
    const hasExcludedActivities = Object.values(globalExclusions || {}).some(v => v === true);
    if (!hasExcludedActivities) return;
    setShowExclusionsReminder(true);
    setExclusionsPromptShown(true);
  }, [setupDone, loading, exclusionsPromptShown, globalExclusions]);

  useEffect(() => {
    if (!setupDone || loading || showExclusionsReminder || guidelinesShown) return;
    setShowGuidelinesPopup(true);
    setGuidelinesShown(true);
  }, [setupDone, loading, showExclusionsReminder, guidelinesShown]);

  // ====== Helpers ======
  const isDateFermee = useCallback((dateStr) => {
    const d = new Date(dateStr + "T12:00:00");
    const dow = d.getDay();
    return dow === 0 || dow === 6 || isFerie(dateStr);
  }, []);

  const getGardesForDay = useCallback((dateStr) => {
    if (!planningData?.gardesAll) return [];
    return planningData.gardesAll[dateStr] || [];
  }, [planningData]);

  const getMyGardeForDay = useCallback((dateStr) => {
    if (!planningData?.gardes) return null;
    return planningData.gardes[dateStr] || null;
  }, [planningData]);

  const GARDE_ACTIVITE_ASSOCIEE = {
    PR1: { id: "sdn-13950", nom: "MAR 1 Salle de naissance" },
    PR2: { id: "mar-2-sdn", nom: "MAR 2 Salle de naissance" },
  };

  const getAssignedForDay = useCallback((actId, dateStr) => {
    if (!planningData?.affectations) return null;
    const amKey = `${actId}_${dateStr}_AM`;
    const pmKey = `${actId}_${dateStr}_PM`;
    const jourKey = `${actId}_${dateStr}_JOUR`;
    return planningData.affectations[amKey] || planningData.affectations[pmKey] || planningData.affectations[jourKey] || null;
  }, [planningData]);

  const getOccupiedActivitiesForDay = useCallback((dateStr) => {
    if (!planningData?.affectations) return {};
    const occupied = {};
    for (const [cle, val] of Object.entries(planningData.affectations)) {
      if (!cle.includes(dateStr)) continue;
      const parts = cle.split("_" + dateStr);
      if (parts.length < 2) continue;
      const actId = parts[0];
      if (val && val !== "FERMÉ") {
        if (!occupied[actId]) occupied[actId] = val;
      }
    }
    return occupied;
  }, [planningData]);

  const isActivityOpenForDay = useCallback((actId, dateStr) => {
    if (!planningData?.ferme) return true;
    const amKey = `${actId}_${dateStr}_AM`;
    const pmKey = `${actId}_${dateStr}_PM`;
    const amClosed = planningData.ferme[amKey];
    const pmClosed = planningData.ferme[pmKey];
    return !amClosed || !pmClosed;
  }, [planningData]);

  const computeJoursDataWithExclusionsDelta = useCallback((baseJoursData, beforeExclusions, afterExclusions) => {
    const changedIds = new Set([
      ...Object.keys(beforeExclusions || {}),
      ...Object.keys(afterExclusions || {}),
    ].filter((id) => Boolean(beforeExclusions?.[id]) !== Boolean(afterExclusions?.[id])));

    if (changedIds.size === 0) return baseJoursData || {};

    const source = baseJoursData || {};
    const next = { ...source };
    Object.entries(source).forEach(([dateStr, dayData]) => {
      if (!dayData || typeof dayData !== "object") return;

      const updatedDay = { ...dayData };
      let changedDay = false;

      ["am", "pm"].forEach((period) => {
        const half = dayData?.[period];
        if (!half || half.statut !== "disponible") return;

        const nextActs = { ...(half.activites || {}) };
        let changedHalf = false;

        changedIds.forEach((actId) => {
          if (!isActivityOpenForDay(actId, dateStr)) return;
          const excludedAfter = Boolean(afterExclusions?.[actId]);
          const targetValue = !excludedAfter;
          if (nextActs[actId] !== targetValue) {
            nextActs[actId] = targetValue;
            changedHalf = true;
          }
        });

        if (changedHalf) {
          updatedDay[period] = { ...half, activites: nextActs };
          changedDay = true;
        }
      });

      if (changedDay) {
        next[dateStr] = updatedDay;
      }
    });

    return next;
  }, [isActivityOpenForDay]);

  // ====== Popup jour ======
  const migrateOldFormat = (existing) => {
    if (existing?.am || existing?.pm) return existing;
    if (!existing?.statut) return null;
    const half = { statut: existing.statut, activites: existing.activites || {}, detail: existing.detail || "" };
    return { am: { ...half }, pm: { ...half } };
  };

  const emptyHalf = () => ({ statut: null, activites: {}, detail: "", subPopup: null });

  const openDayPopup = useCallback((dateStr) => {
    if (isDateFermee(dateStr)) return;
    setSelectedDay(dateStr);
    const raw = joursData[dateStr];
    const existing = migrateOldFormat(raw);
    if (existing) {
      setPopupAM({ statut: existing.am?.statut || null, activites: existing.am?.activites || {}, detail: existing.am?.detail || "", subPopup: null });
      setPopupPM({ statut: existing.pm?.statut || null, activites: existing.pm?.activites || {}, detail: existing.pm?.detail || "", subPopup: null });
    } else {
      setPopupAM(emptyHalf());
      setPopupPM(emptyHalf());
    }
  }, [joursData, isDateFermee]);

  const buildActsForDisponible = useCallback((dateStr) => {
    const occupied = dateStr ? getOccupiedActivitiesForDay(dateStr) : {};
    const acts = {};
    ACTIVITES_VISIBLES.forEach(a => {
      if (a.type !== "garde" && dateStr && isActivityOpenForDay(a.id, dateStr)) {
        acts[a.id] = occupied[a.id] ? false : !globalExclusions[a.id];
      }
    });
    return acts;
  }, [ACTIVITES_VISIBLES, getOccupiedActivitiesForDay, isActivityOpenForDay, globalExclusions]);

  const handleStatutSelect = (period, statutId) => {
    const setter = period === "am" ? setPopupAM : setPopupPM;
    if (statutId === "disponible") {
      setter({ statut: statutId, activites: buildActsForDisponible(selectedDay), detail: "", subPopup: null });
    } else if (statutId === "cf") {
      setter({ statut: statutId, activites: {}, detail: "", subPopup: "cf" });
    } else if (statutId === "enseignement") {
      setter({ statut: statutId, activites: {}, detail: "", subPopup: "enseignement" });
    } else if (statutId === "autre") {
      setter({ statut: statutId, activites: {}, detail: "", subPopup: "autre" });
    } else {
      setter({ statut: statutId, activites: {}, detail: "", subPopup: null });
    }
  };

  const togglePopupActivity = (period, actId) => {
    const setter = period === "am" ? setPopupAM : setPopupPM;
    setter(prev => {
      const next = { ...prev, activites: { ...prev.activites } };
      const newVal = !prev.activites[actId];
      next.activites[actId] = newVal;
      getLinkedIds(actId).forEach(linked => {
        if (selectedDay && isActivityOpenForDay(linked, selectedDay)) {
          next.activites[linked] = newVal;
        }
      });
      return next;
    });
  };

  const doSaveDayAndNext = (goNext) => {
    setHalfDayWarning(null);
    setJoursData(prev => ({
      ...prev,
      [selectedDay]: {
        am: { statut: popupAM.statut, activites: popupAM.statut === "disponible" ? popupAM.activites : {}, detail: popupAM.detail },
        pm: { statut: popupPM.statut, activites: popupPM.statut === "disponible" ? popupPM.activites : {}, detail: popupPM.detail },
      },
    }));

    if (goNext) {
      const sortedDays = workDays.map(d => toISO(d));
      const currentIdx = sortedDays.indexOf(selectedDay);
      if (currentIdx >= 0 && currentIdx < sortedDays.length - 1) {
        openDayPopup(sortedDays[currentIdx + 1]);
        return;
      }
    }
    setSelectedDay(null);
  };

  const saveDayAndNext = (goNext) => {
    if (!selectedDay || (!popupAM.statut && !popupPM.statut)) return;

    const amMissing = !popupAM.statut;
    const pmMissing = !popupPM.statut;
    if (amMissing || pmMissing) {
      const missing = amMissing ? "le matin" : "l'après-midi";
      setHalfDayWarning({ goNext, missing });
      return;
    }
    doSaveDayAndNext(goNext);
  };

  const saveCurrentPopup = () => {
    if (!selectedDay || (!popupAM.statut && !popupPM.statut)) return;
    setJoursData(prev => ({
      ...prev,
      [selectedDay]: {
        am: { statut: popupAM.statut, activites: popupAM.statut === "disponible" ? popupAM.activites : {}, detail: popupAM.detail },
        pm: { statut: popupPM.statut, activites: popupPM.statut === "disponible" ? popupPM.activites : {}, detail: popupPM.detail },
      },
    }));
  };

  const goToPrevDay = () => {
    if (!selectedDay) return;
    saveCurrentPopup();
    const sortedDays = workDays.map(d => toISO(d));
    const currentIdx = sortedDays.indexOf(selectedDay);
    if (currentIdx > 0) {
      openDayPopup(sortedDays[currentIdx - 1]);
    }
  };

  const goToNextDay = () => {
    if (!selectedDay) return;
    saveCurrentPopup();
    const sortedDays = workDays.map(d => toISO(d));
    const currentIdx = sortedDays.indexOf(selectedDay);
    if (currentIdx >= 0 && currentIdx < sortedDays.length - 1) {
      openDayPopup(sortedDays[currentIdx + 1]);
    }
  };

  const persistStep1Changes = useCallback(async (nextJoursData, nextGlobalExclusions) => {
    if (!user?.nom || !user?.prenom || !MOIS_CLE) return false;
    try {
      const res = await fetch(PLANNING_PR_DESIDERATA_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mois: MOIS_CLE,
          medecin: { nom: user.nom, prenom: user.prenom, medecin_id: user.medecin_id || null },
          souhaits: {
            version: 3,
            jours: nextJoursData,
            globalExclusions: nextGlobalExclusions,
            joursSupplementaires,
          },
        }),
      });
      return res.ok;
    } catch (e) {
      console.warn("Sauvegarde automatique étape 1 impossible:", e);
      return false;
    }
  }, [MOIS_CLE, user, joursSupplementaires]);

  // ====== Sauvegarde ======
  const handleSave = async () => {
    if (!user?.nom || !user?.prenom || !MOIS_CLE) return;

    const isDayFilled = (d) => { const j = joursData[toISO(d)]; return j?.am?.statut && j?.pm?.statut; };
    const isDayPartial = (d) => { const j = joursData[toISO(d)]; return (j?.am?.statut || j?.pm?.statut) && !(j?.am?.statut && j?.pm?.statut); };
    const isDayEmpty = (d) => { const j = joursData[toISO(d)]; return !j?.am?.statut && !j?.pm?.statut && !j?.statut; };
    const remaining = workDays.filter(d => !isDayFilled(d)).length;
    const partialDays = workDays.filter(d => isDayPartial(d)).length;
    const emptyDays = workDays.filter(d => isDayEmpty(d)).length;

    if (remaining > 0) {
      let details = "";
      if (partialDays > 0 && emptyDays > 0) {
        details = `Il reste ${emptyDays} jour${emptyDays > 1 ? "s" : ""} non renseigné${emptyDays > 1 ? "s" : ""} et ${partialDays} jour${partialDays > 1 ? "s" : ""} avec une seule demi-journée renseignée.\nVoulez-vous sauvegarder quand même ?`;
      } else if (partialDays > 0) {
        details = `${partialDays} jour${partialDays > 1 ? "s" : ""} n'${partialDays > 1 ? "ont" : "a"} qu'une seule demi-journée renseignée.\nVoulez-vous sauvegarder quand même ?`;
      } else {
        details = `Il reste ${emptyDays} jour${emptyDays > 1 ? "s" : ""} ouvré${emptyDays > 1 ? "s" : ""} sans désidérata.\nVoulez-vous sauvegarder quand même ?`;
      }
      setShowModal({
        type: "warning",
        message: `${remaining} jour${remaining > 1 ? "s" : ""} incomplet${remaining > 1 ? "s" : ""}`,
        details,
        onConfirm: doSave,
      });
      return;
    }
    setShowJoursSupPopup(true);
  };

  const doSave = async () => {
    setSaving(true);
    setShowModal(null);
    setShowJoursSupPopup(false);
    try {
      const response = await fetch(PLANNING_PR_DESIDERATA_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mois: MOIS_CLE,
          medecin: { nom: user.nom, prenom: user.prenom, medecin_id: user.medecin_id || null },
          souhaits: { version: 3, jours: joursData, globalExclusions, joursSupplementaires },
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      setLastAppliedExclusions(globalExclusions || {});
      setBaselineSnapshot(JSON.stringify({
        joursData,
        globalExclusions: globalExclusions || {},
        joursSupplementaires,
      }));
      runPendingAfterSaveIfAny();
      setShowModal({
        type: "success",
        message: "Désidératas sauvegardés !",
        details: `Vos désidératas pour ${moisLabel(MOIS_CLE)} ont été enregistrés avec succès.${joursSupplementaires > 0 ? `\n(+ ${joursSupplementaires} jour${joursSupplementaires > 1 ? "s" : ""} d'activité${joursSupplementaires > 1 ? "s" : ""} supplémentaire${joursSupplementaires > 1 ? "s" : ""})` : ""}`,
      });
    } catch {
      pendingAfterSaveRef.current = null;
      setShowModal({
        type: "error",
        message: "Erreur lors de la sauvegarde",
        details: "Veuillez réessayer ultérieurement.",
      });
    } finally {
      setSaving(false);
    }
  };

  const getHalfStatut = (jour, period) => {
    if (jour?.am && jour?.pm) return jour[period]?.statut;
    return jour?.statut || null;
  };

  // ====== Stats ======
  const stats = useMemo(() => {
    let filled = 0, halves = { disponible: 0, indisponible: 0, "ca-rtt": 0, cf: 0, enseignement: 0, inserm: 0, autre: 0 };
    workDays.forEach(d => {
      const jour = joursData[toISO(d)];
      const amSt = getHalfStatut(jour, "am");
      const pmSt = getHalfStatut(jour, "pm");
      if (amSt || pmSt) {
        filled++;
        [amSt, pmSt].forEach(s => { if (s && halves[s] !== undefined) halves[s]++; });
      }
    });
    return { total: workDays.length, filled, remaining: workDays.length - filled, ...halves };
  }, [workDays, joursData]);

  // ====== Position actuelle dans la liste de jours ouvrés ======
  const dayPosition = useMemo(() => {
    if (!selectedDay) return { current: 0, total: 0 };
    const sorted = workDays.map(d => toISO(d));
    return { current: sorted.indexOf(selectedDay) + 1, total: sorted.length };
  }, [selectedDay, workDays]);

  // ====== Rendu conditionnel ======
  if (cfgLoading || !activitesLoaded) {
    return <div style={S.page}><div style={S.container}><p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>Chargement...</p></div></div>;
  }

  if (accessDenied) {
    return null;
  }

  if (isAccessRestreint && !isAutorise) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign: "center", padding: "60px 24px" }}>
          <div style={{ fontSize: "36px", marginBottom: "16px", color: "#6b7280" }}>Accès restreint</div>
          <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", marginBottom: "16px" }}>Fonctionnalité en cours de développement</h2>
          <p style={{ color: "#6b7280", marginBottom: "24px" }}>Cette fonctionnalité sera bientôt disponible.</p>
          <button onClick={() => (window.location.href = "/portail-medecin")} style={S.btnBack}>Retour</button>
        </div>
      </div>
    );
  }

  if (moisOuverts.length === 0) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign: "center", padding: "60px 24px" }}>
          <div style={{ fontSize: "36px", marginBottom: "16px", color: "#6b7280" }}>--</div>
          <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", marginBottom: "16px" }}>Désidératas non ouverts</h2>
          <p style={{ color: "#6b7280", marginBottom: "24px" }}>Aucun mois n'est ouvert pour la saisie.</p>
          <button onClick={() => (window.location.href = "/portail-medecin")} style={S.btnBack}>Retour</button>
        </div>
      </div>
    );
  }

  // ====== RENDU PRINCIPAL ======
  return (
    <div style={S.page}>
      {/* En-tête */}
      <div style={S.header}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
          <h1 style={S.title}>Désidératas Planning Port-Royal</h1>
          {user && (
            <span style={{ color: "#fbbf24", fontSize: "20px", fontWeight: 800, marginTop: "2px" }}>
              Dr {user.prenom} {user.nom}
            </span>
          )}
        </div>
        <LogoutButton />
      </div>

      <div style={S.container}>
        {showOphPlanningShortcutCard && (
          <div
            style={{
              marginBottom: "14px",
              padding: "14px 18px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #eef2ff 100%)",
              border: "1px solid #7dd3fc",
              boxShadow: "0 2px 10px rgba(14, 165, 233, 0.12)",
            }}
          >
            <div style={{ fontWeight: 800, fontSize: "16px", color: "#0c4a6e", marginBottom: "6px" }}>
              Ophtalmologie — planning
            </div>
            <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#334155", lineHeight: 1.5 }}>
              Accès rapide vers l’édition du planning OPH sur l’interface multisecteurs (nouvel onglet).
              Ce n’est pas la supervision générale : page dédiée au secteur ophtalmologie.
            </p>
            <button
              type="button"
              disabled={openingOphPlanning}
              onClick={openOphPlanningMultisecteur}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border: "none",
                background: openingOphPlanning ? "#94a3b8" : "#0369a1",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 700,
                cursor: openingOphPlanning ? "not-allowed" : "pointer",
                boxShadow: "0 2px 8px rgba(3, 105, 161, 0.35)",
              }}
            >
              {openingOphPlanning ? "Connexion…" : "Ouvrir le planning OPH"}
            </button>
          </div>
        )}
        {/* Toolbar */}
        <div style={S.toolbar}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <span style={{ fontWeight: 800, fontSize: "24px", color: "#111827" }}>{moisLabel(MOIS_CLE)}</span>
            {moisOuverts.length > 1 && (
              <select
                value={selectedMois}
                onChange={(e) => trySelectMois(String(e.target.value || ""))}
                style={{
                  padding: "7px 10px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontWeight: 600,
                  background: "#ffffff",
                  color: "#0f172a",
                  minWidth: "220px",
                }}
              >
                {moisOuverts.map((m) => (
                  <option key={String(m.mois_cle)} value={String(m.mois_cle)}>
                    {m.mois_label || m.mois_cle}
                  </option>
                ))}
              </select>
            )}
            <button type="button" onClick={tryGoPortail} style={S.btnRetourToolbar}>
              {"← Portail médecin"}
            </button>
          </div>
          {setupDone && (
            <div style={{ display: "flex", justifyContent: "center", flex: 1 }}>
              <button
                onClick={() => setSetupDone(false)}
                style={{
                  padding: "11px 22px",
                  borderRadius: "10px",
                  border: "2px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.16)",
                }}
              >
                {"Modifier les activités exclues"}
              </button>
            </div>
          )}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {stats.remaining > 0 && (
              <span style={{ color: "#d97706", fontWeight: 600, fontSize: "14px" }}>
                {stats.remaining} jour{stats.remaining > 1 ? "s" : ""} ouvré{stats.remaining > 1 ? "s" : ""} restant{stats.remaining > 1 ? "s" : ""} / {stats.total}
              </span>
            )}
            <button onClick={handleSave} disabled={saving} style={S.btnSave}>
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        </div>

        {/* ====== ÉTAPE 1 : SÉLECTION GLOBALE DES ACTIVITÉS ====== */}
        {!setupDone && !loading && (
          <div style={S.setupWrapper}>
            <div style={S.setupHeader}>
              <h2 style={{ fontSize: "28px", fontWeight: 800, color: "#1e293b", margin: "0 0 10px 0" }}>
                {"Étape 1 — Activités du planning Port-Royal"}
              </h2>
              <p style={{ fontSize: "18px", color: "#4b5563", margin: 0, lineHeight: 1.6 }}>
                {"Voici la liste des activités séniorisées du planning. Par défaut, toutes sont cochées. "}
                {"Décochez celles auxquelles vous ne souhaitez pas participer (en accord avec votre responsable de secteur). "}
                {"Les activités exclues ici seront automatiquement décochées chaque jour."}
              </p>
            </div>

            <div style={S.setupGrid}>
              {ACTIVITES.map((act) => {
                if (act.type === "separator") {
                  return <div key={act.id} style={{ gridColumn: "1 / -1", height: "4px", background: "#e5e7eb", borderRadius: "2px", margin: "4px 0" }}></div>;
                }
                if (act.type === "garde") return null;
                const excluded = globalExclusions[act.id] === true;
                const linked = getLinkedIds(act.id);
                return (
                  <div
                    key={act.id}
                    onClick={() => {
                      setGlobalExclusions(prev => {
                        const next = { ...prev };
                        const newVal = !excluded;
                        next[act.id] = newVal;
                        linked.forEach(lid => { next[lid] = newVal; });
                        return next;
                      });
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "12px 16px", borderRadius: "10px",
                      background: excluded ? "#fef2f2" : "#f0fdf4",
                      border: excluded ? "2px solid #f87171" : "2px solid #059669",
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <span style={{
                      width: "26px", height: "26px", borderRadius: "5px",
                      border: `2px solid ${excluded ? "#f87171" : "#059669"}`,
                      background: excluded ? "#ffffff" : "#059669",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "15px", color: "#fff", flexShrink: 0,
                    }}>
                      {!excluded ? "✓" : ""}
                    </span>
                    <span style={{
                      fontSize: "18px", fontWeight: 600,
                      color: excluded ? "#991b1b" : "#065f46",
                    }}>
                      {act.nom}
                      {linked.length > 0 && <span style={{ fontSize: "13px", color: "#6b7280", marginLeft: "6px" }}>(lié)</span>}
                    </span>
                    {excluded && <span style={{ fontSize: "15px", color: "#dc2626", marginLeft: "auto", fontWeight: 600 }}>Exclu</span>}
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "24px" }}>
              <button
                onClick={async () => {
                  const nextJoursData = computeJoursDataWithExclusionsDelta(
                    joursData,
                    lastAppliedExclusions || {},
                    globalExclusions || {}
                  );
                  const nextGlob = globalExclusions || {};
                  setJoursData(nextJoursData);
                  setLastAppliedExclusions(nextGlob);
                  const ok = await persistStep1Changes(nextJoursData, nextGlob);
                  if (ok) {
                    setBaselineSnapshot(JSON.stringify({
                      joursData: nextJoursData,
                      globalExclusions: nextGlob,
                      joursSupplementaires,
                    }));
                  }
                  setSetupDone(true);
                }}
                style={{
                  padding: "16px 48px", borderRadius: "10px", border: "none",
                  background: "#059669", color: "#ffffff", fontSize: "18px", fontWeight: 700,
                  cursor: "pointer", boxShadow: "0 4px 12px rgba(5,150,105,0.3)",
                }}
              >
                {"Valider et passer au calendrier →"}
              </button>
            </div>
          </div>
        )}

        {/* ====== ÉTAPE 2 : CALENDRIER JOUR PAR JOUR ====== */}
        {setupDone && (
          <>
            {/* Légende */}
            <div style={S.legend}>
              {STATUTS.map(s => (
                <span key={s.id} style={S.legendItem}>
                  <span style={{ ...S.legendDot, background: s.bg, border: `2px solid ${s.color}` }}></span>
                  {s.label}
                </span>
              ))}
              <span style={S.legendItem}>
                <span style={{ ...S.legendDot, background: "#e5e7eb", border: "2px solid #9ca3af" }}></span>
                Fermé / WE
              </span>
            </div>

          </>
        )}

        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>Chargement du planning...</div>
        ) : setupDone ? (
          /* ====== CALENDRIER ====== */
          <div style={S.calendarWrapper}>
            {/* En-tête jours de semaine */}
            <div style={S.calendarHeader}>
              {JOURS_SEMAINE.map((j, i) => (
                <div key={i} style={{
                  ...S.calendarHeaderCell,
                  color: i >= 5 ? "#dc2626" : "#1e293b",
                }}>
                  {j}
                </div>
              ))}
            </div>

            {/* Semaines */}
            {calendarWeeks.map((week, wIdx) => (
              <div key={wIdx} style={S.calendarRow}>
                {week.map((day, dIdx) => {
                  if (!day) return <div key={dIdx} style={S.calendarCellEmpty}></div>;

                  const iso = toISO(day);
                  const dow = day.getDay();
                  const ferie = isFerie(iso);
                  const isWE = dow === 0 || dow === 6;
                  const closed = isWE || ferie;
                  const jourData = joursData[iso];
                  const amSt = getHalfStatut(jourData, "am");
                  const pmSt = getHalfStatut(jourData, "pm");
                  const statutAM = amSt ? STATUT_MAP[amSt] : null;
                  const statutPM = pmSt ? STATUT_MAP[pmSt] : null;
                  const hasSome = statutAM || statutPM;
                  const mainColor = statutAM?.color || statutPM?.color || null;
                  const gardesJour = getGardesForDay(iso);
                  const myGarde = getMyGardeForDay(iso);

                  return (
                    <div
                      key={dIdx}
                      style={{
                        ...S.calendarCell,
                        background: closed ? "#f3f4f6"
                          : hasSome ? (statutAM?.bgLight || statutPM?.bgLight || "#ffffff")
                          : "#ffffff",
                        border: hasSome ? `2px solid ${mainColor}` : "2px solid #e5e7eb",
                        cursor: closed ? "default" : "pointer",
                        opacity: closed ? 0.6 : 1,
                      }}
                      onClick={() => !closed && openDayPopup(iso)}
                      title={closed ? (ferie ? `Férié : ${getFrenchHolidayName(iso) || "Férié"}` : "Week-end") : "Cliquer pour renseigner ce jour"}
                    >
                      {/* Date */}
                      <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                        marginBottom: "2px",
                      }}>
                        <span style={{
                          fontSize: "19px", fontWeight: 800,
                          color: closed ? "#9ca3af" : mainColor || "#374151",
                        }}>
                          {day.getDate()}
                        </span>
                        {myGarde && (
                          <span style={{
                            fontSize: "11px", fontWeight: 800,
                            background: "#fef3c7", color: "#92400e",
                            padding: "2px 6px", borderRadius: "5px",
                          }}>
                            GARDE {myGarde}
                          </span>
                        )}
                      </div>

                      {/* Statuts AM/PM */}
                      {hasSome && !closed && (
                        <div style={{ display: "flex", gap: "2px", marginBottom: "2px" }}>
                          <div style={{
                            flex: 1, fontSize: "12px", fontWeight: 800,
                            color: statutAM ? statutAM.color : "#9ca3af",
                            textAlign: "center", padding: "2px 1px",
                            background: statutAM ? statutAM.bg : "#f3f4f6",
                            borderRadius: "3px",
                          }}>
                            {statutAM ? statutAM.short || statutAM.label : "—"}
                          </div>
                          <div style={{
                            flex: 1, fontSize: "12px", fontWeight: 800,
                            color: statutPM ? statutPM.color : "#9ca3af",
                            textAlign: "center", padding: "2px 1px",
                            background: statutPM ? statutPM.bg : "#f3f4f6",
                            borderRadius: "3px",
                          }}>
                            {statutPM ? statutPM.short || statutPM.label : "—"}
                          </div>
                        </div>
                      )}

                      {/* Non renseigné */}
                      {!hasSome && !closed && (
                        <div style={{
                          fontSize: "13px", color: "#9ca3af", textAlign: "center",
                          padding: "4px 0", fontStyle: "italic",
                        }}>
                          Non renseigné
                        </div>
                      )}

                      {/* Férié / WE */}
                      {closed && (
                        <div style={{ fontSize: "12px", color: "#9ca3af", textAlign: "center", padding: "2px 0" }}>
                          {ferie ? "Férié" : isWE ? (dow === 6 ? "Samedi" : "Dimanche") : ""}
                        </div>
                      )}

                      {/* Gardes du jour */}
                      {gardesJour.length > 0 && (
                        <div style={{ marginTop: "auto", paddingTop: "2px" }}>
                          {gardesJour.map((g, i) => (
                            <div key={i} style={{
                              fontSize: "10px", color: "#854d0e",
                              wordBreak: "break-word",
                            }}>
                              {g.type}: {formatCode(g.code)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : null}

      </div>

      {/* ====== POPUP JOUR ====== */}
      {selectedDay && (
        <div style={S.overlay} onClick={() => setSelectedDay(null)}>
          <div style={S.popup} onClick={e => e.stopPropagation()}>
            {/* Header popup */}
            <div style={S.popupHeader}>
              <button onClick={goToPrevDay} disabled={dayPosition.current <= 1} style={{
                ...S.popupNavBtn,
                opacity: dayPosition.current <= 1 ? 0.3 : 1,
              }}>{"◀"}</button>
              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: "22px", fontWeight: 800, color: "#1e293b" }}>
                  {formatDateLong(selectedDay)}
                </div>
                <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                  Jour {dayPosition.current} / {dayPosition.total}
                </div>
              </div>
              <button onClick={goToNextDay} disabled={dayPosition.current >= dayPosition.total} style={{
                ...S.popupNavBtn,
                opacity: dayPosition.current >= dayPosition.total ? 0.3 : 1,
              }}>{"▶"}</button>
              <button onClick={() => setSelectedDay(null)} style={S.popupCloseBtn}>{"✕"}</button>
            </div>

            {/* Gardes du jour + activités associées */}
            {(() => {
              const gardes = getGardesForDay(selectedDay);
              const myGarde = getMyGardeForDay(selectedDay);
              if (gardes.length === 0 && !myGarde) return null;
              return (
                <div style={S.gardeSection}>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: "#92400e", marginBottom: "6px" }}>
                    Médecins de garde ce jour
                  </div>
                  {myGarde && (
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#dc2626", marginBottom: "2px" }}>
                      &rarr; Vous êtes de garde {myGarde}
                    </div>
                  )}
                  {gardes.filter(g => !myGarde || g.code !== "").map((g, i) => {
                    const assoc = GARDE_ACTIVITE_ASSOCIEE[g.type];
                    const assocDoc = assoc ? getAssignedForDay(assoc.id, selectedDay) : null;
                    return (
                      <div key={i} style={{ fontSize: "13px", color: "#78350f", marginBottom: "3px" }}>
                        <div>
                          Garde {g.type} : <strong>{formatCode(g.code)}</strong>
                        </div>
                        {assocDoc && (
                          <div style={{ fontSize: "11px", color: "#92400e", marginLeft: "16px", fontStyle: "italic" }}>
                            {assoc.nom} : {assocDoc}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Sections AM / PM */}
            {["am", "pm"].map(period => {
              const half = period === "am" ? popupAM : popupPM;
              const setHalf = period === "am" ? setPopupAM : setPopupPM;
              const periodLabel = period === "am" ? "MATIN" : "APRÈS-MIDI";
              const periodColor = period === "am" ? "#1e40af" : "#7c2d12";
              const occupied = getOccupiedActivitiesForDay(selectedDay);
              return (
                <div key={period} style={{
                  marginBottom: "12px", padding: "12px", borderRadius: "10px",
                  border: `2px solid ${half.statut ? (STATUT_MAP[half.statut]?.color || "#e5e7eb") : "#e5e7eb"}`,
                  background: half.statut ? (STATUT_MAP[half.statut]?.bgLight || "#fafafa") : "#fafafa",
                }}>
                  <div style={{ fontSize: "14px", fontWeight: 800, color: periodColor, marginBottom: "8px", letterSpacing: "1px" }}>
                    {periodLabel}
                  </div>

                  {/* Boutons statut */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
                    {STATUTS.map(s => (
                      <button
                        key={s.id}
                        onClick={() => handleStatutSelect(period, s.id)}
                        style={{
                          padding: "6px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: 700,
                          background: half.statut === s.id ? s.bg : "#ffffff",
                          border: half.statut === s.id ? `2px solid ${s.color}` : "1px solid #d1d5db",
                          color: half.statut === s.id ? s.color : "#6b7280",
                          cursor: "pointer", transition: "all 0.15s",
                        }}
                      >
                        {s.short || s.label}
                      </button>
                    ))}
                  </div>

                  {/* Sub-popup CF */}
                  {half.subPopup === "cf" && (
                    <div style={{ ...S.subSection, marginTop: 0, marginBottom: "6px" }}>
                      <div style={{ fontSize: "12px", color: "#7c3aed", fontWeight: 600, marginBottom: "4px" }}>
                        Rappel : justificatif de congé de formation requis.
                      </div>
                      <input type="text" placeholder="Type de formation (optionnel)"
                        value={half.detail} onChange={e => setHalf(p => ({ ...p, detail: e.target.value }))}
                        style={{ ...S.inputField, fontSize: "12px", padding: "6px 10px" }} />
                    </div>
                  )}

                  {/* Sub-popup Enseignement */}
                  {half.subPopup === "enseignement" && (
                    <div style={{ ...S.subSection, marginTop: 0, marginBottom: "6px" }}>
                      <div style={{ fontSize: "12px", color: "#2563eb", fontWeight: 600, marginBottom: "4px" }}>
                        Précisez le type d'enseignement :
                      </div>
                      <input type="text" placeholder="DU, DIU, cours, simulation..."
                        value={half.detail} onChange={e => setHalf(p => ({ ...p, detail: e.target.value }))}
                        style={{ ...S.inputField, fontSize: "12px", padding: "6px 10px" }} />
                    </div>
                  )}

                  {/* Sub-popup Autre */}
                  {half.subPopup === "autre" && (
                    <div style={{ ...S.subSection, marginTop: 0, marginBottom: "6px" }}>
                      <textarea placeholder="Précisez votre situation..."
                        value={half.detail} onChange={e => setHalf(p => ({ ...p, detail: e.target.value }))}
                        style={{ ...S.inputField, fontSize: "12px", padding: "6px 10px", minHeight: "50px", resize: "vertical" }} />
                    </div>
                  )}

                  {/* Activités si Disponible */}
                  {half.statut === "disponible" && (
                    <div style={{ marginTop: "6px" }}>
                      <div style={{ fontSize: "12px", fontWeight: 700, color: "#065f46", marginBottom: "4px" }}>
                        Activités (décochez celles non souhaitées)
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {ACTIVITES.map(act => {
                          if (act.type === "separator") return null;
                          if (act.type === "garde") return null;
                          const isOpen = isActivityOpenForDay(act.id, selectedDay);
                          const assignedTo = occupied[act.id];
                          const isOccupied = !!assignedTo;
                          const checked = half.activites[act.id] === true;
                          const disabled = !isOpen || isOccupied;
                          return (
                            <div key={act.id}
                              onClick={() => !disabled && togglePopupActivity(period, act.id)}
                              style={{
                                display: "flex", alignItems: "center", gap: "4px",
                                padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 600,
                                background: disabled ? "#f3f4f6" : checked ? "#d1fae5" : "#fecaca",
                                border: isOccupied ? "1px solid #818cf8" : disabled ? "1px solid #d1d5db" : checked ? "1px solid #059669" : "1px solid #f87171",
                                cursor: disabled ? "default" : "pointer",
                                opacity: disabled ? 0.5 : 1,
                                color: disabled ? "#9ca3af" : checked ? "#065f46" : "#991b1b",
                              }}>
                              <span style={{
                                width: "14px", height: "14px", borderRadius: "3px", flexShrink: 0,
                                border: `2px solid ${disabled ? "#9ca3af" : checked ? "#059669" : "#f87171"}`,
                                background: disabled ? "#e5e7eb" : checked ? "#059669" : "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "9px", color: "#fff",
                              }}>
                                {isOccupied ? "—" : checked ? "✓" : ""}
                              </span>
                              {act.nom}
                              {isOccupied && <span style={{ fontSize: "9px", color: "#6366f1" }}>{assignedTo}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Avertissement demi-journée manquante */}
            {halfDayWarning && (
              <div style={{
                margin: "10px 0", padding: "12px 16px", borderRadius: "8px",
                background: "#fffbeb", border: "2px solid #f59e0b",
              }}>
                <div style={{ fontSize: "14px", fontWeight: 700, color: "#92400e", marginBottom: "6px" }}>
                  Attention : {halfDayWarning.missing} n'est pas renseigné(e)
                </div>
                <div style={{ fontSize: "13px", color: "#78350f", marginBottom: "10px" }}>
                  Souhaitez-vous valider uniquement la demi-journée renseignée, ou compléter les deux demi-journées ?
                </div>
                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                  <button onClick={() => setHalfDayWarning(null)}
                    style={{ padding: "6px 16px", borderRadius: "6px", border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                    Compléter
                  </button>
                  <button onClick={() => doSaveDayAndNext(halfDayWarning.goNext)}
                    style={{ padding: "6px 16px", borderRadius: "6px", border: "none", background: "#f59e0b", color: "#fff", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                    Valider quand même
                  </button>
                </div>
              </div>
            )}

            {/* Boutons de validation */}
            <div style={S.popupFooter}>
              <button onClick={() => { setSelectedDay(null); setHalfDayWarning(null); }} style={S.btnCancel}>
                Fermer
              </button>
              {(popupAM.statut || popupPM.statut) && (
                <>
                  <button onClick={() => saveDayAndNext(false)} style={S.btnValidate}>
                    Valider
                  </button>
                  {dayPosition.current < dayPosition.total && (
                    <button onClick={() => saveDayAndNext(true)} style={S.btnValidateNext}>
                      Valider et jour suivant &rarr;
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== POPUP JOURS SUPPLÉMENTAIRES ====== */}
      {showJoursSupPopup && (
        <div style={S.overlay} onClick={() => setShowJoursSupPopup(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "24px", fontWeight: 700, color: "#1e40af", textAlign: "center", marginBottom: "8px" }}>
              Temps de travail additionnel
            </div>
            <div style={{ fontSize: "15px", color: "#374151", textAlign: "center", marginBottom: "12px", lineHeight: 1.6 }}>
              Souhaitez-vous réaliser du <strong>temps de travail additionnel</strong> au mois de {moisLabel(MOIS_CLE)} ?
            </div>
            <div style={{ fontSize: "13px", color: "#6b7280", textAlign: "center", marginBottom: "16px", lineHeight: 1.5, fontStyle: "italic" }}>
              Vos activités en dehors de Port Royal seront prises en compte le cas échéant.
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
              {[0, 1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setJoursSupplementaires(n)}
                  style={{
                    width: "56px", height: "56px", borderRadius: "10px", fontSize: "20px", fontWeight: 700,
                    border: joursSupplementaires === n ? "3px solid #2563eb" : "2px solid #d1d5db",
                    background: joursSupplementaires === n ? "#dbeafe" : "#ffffff",
                    color: joursSupplementaires === n ? "#1e40af" : "#6b7280",
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151", textAlign: "center", marginBottom: "16px" }}>
              {joursSupplementaires === 0
                ? "Aucune activité supplémentaire"
                : `${joursSupplementaires} jour${joursSupplementaires > 1 ? "s" : ""} d'activité${joursSupplementaires > 1 ? "s" : ""} supplémentaire${joursSupplementaires > 1 ? "s" : ""}`}
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button onClick={() => { clearPendingAfterSave(); setShowJoursSupPopup(false); }}
                style={{ ...S.btnCancel, padding: "12px 28px", fontSize: "16px" }}>
                Annuler
              </button>
              <button onClick={doSave}
                style={{ ...S.btnSave, padding: "12px 28px", fontSize: "16px" }}>
                {saving ? "Sauvegarde..." : "Sauvegarder"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== MODAL GÉNÉRALE ====== */}
      {showModal && (
        <div style={S.overlay} onClick={() => { if (showModal.type === "warning") clearPendingAfterSave(); setShowModal(null); }}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "32px", marginBottom: "12px", textAlign: "center", fontWeight: 700,
              color: showModal.type === "success" ? "#059669" : showModal.type === "error" ? "#dc2626" : "#d97706",
            }}>
              {showModal.type === "success" ? "OK" : showModal.type === "error" ? "Erreur" : "Attention"}
            </div>
            <div style={{
              fontSize: "22px", fontWeight: 700, textAlign: "center", marginBottom: "12px",
              color: showModal.type === "success" ? "#065f46" : showModal.type === "error" ? "#991b1b" : "#92400e",
            }}>
              {showModal.message}
            </div>
            {showModal.details && (
              <div style={{ fontSize: "15px", color: "#4b5563", textAlign: "center", marginBottom: "20px", whiteSpace: "pre-line", lineHeight: 1.6 }}>
                {showModal.details}
              </div>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              {showModal.type === "warning" && showModal.onConfirm ? (
                <>
                  <button onClick={() => { showModal.onConfirm(); }} style={{ ...S.btnSave, background: "#f59e0b", padding: "12px 28px", fontSize: "16px" }}>
                    Sauvegarder quand même
                  </button>
                  <button onClick={() => { clearPendingAfterSave(); setShowModal(null); }} style={{ ...S.btnCancel, padding: "12px 28px", fontSize: "16px" }}>
                    Compléter
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowModal(null)}
                  style={{ ...S.btnSave, padding: "14px 40px", fontSize: "18px", background: showModal.type === "success" ? "#059669" : "#dc2626" }}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== MODIFICATIONS NON ENREGISTRÉES (sortie / autre mois) ====== */}
      {unsavedPrompt && (
        <div style={S.overlay} onClick={() => setUnsavedPrompt(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "32px", marginBottom: "12px", textAlign: "center", fontWeight: 700, color: "#d97706" }}>
              Attention
            </div>
            <div style={{ fontSize: "20px", fontWeight: 700, textAlign: "center", marginBottom: "12px", color: "#92400e" }}>
              Modifications non enregistrées
            </div>
            <div style={{ fontSize: "15px", color: "#4b5563", textAlign: "center", marginBottom: "20px", lineHeight: 1.6 }}>
              {unsavedPrompt.mode === "portail"
                ? "Vous avez modifié vos désidératas sans les sauvegarder. Souhaitez-vous les enregistrer avant de quitter ?"
                : `Vous avez des modifications non enregistrées pour ${moisLabel(MOIS_CLE)}. Changer de mois sans sauvegarder les fera perdre pour ce mois.`}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => {
                  const p = unsavedPrompt;
                  pendingAfterSaveRef.current = p.mode === "portail"
                    ? { kind: "portail" }
                    : { kind: "mois", mois: p.nextMois };
                  setUnsavedPrompt(null);
                  handleSave();
                }}
                style={{ ...S.btnSave, padding: "12px 20px", fontSize: "15px" }}
              >
                Sauvegarder
              </button>
              <button
                type="button"
                onClick={() => {
                  if (unsavedPrompt.mode === "portail") {
                    window.location.href = "/portail-medecin";
                  } else {
                    setSelectedMois(unsavedPrompt.nextMois);
                  }
                  setUnsavedPrompt(null);
                }}
                style={{ ...S.btnCancel, padding: "12px 20px", fontSize: "15px", border: "2px solid #b45309", color: "#92400e", fontWeight: 700 }}
              >
                Quitter sans enregistrer
              </button>
              <button
                type="button"
                onClick={() => setUnsavedPrompt(null)}
                style={{ ...S.btnCancel, padding: "12px 20px", fontSize: "15px" }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== RAPPEL ACTIVITÉS EXCLUES ====== */}
      {showExclusionsReminder && (
        <div style={S.overlay} onClick={() => setShowExclusionsReminder(false)}>
          <div style={S.modalBoxLarge} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "58px", marginBottom: "16px", textAlign: "center", color: "#d97706", fontWeight: 800 }}>
              Rappel
            </div>
            <div style={{ fontSize: "42px", fontWeight: 800, textAlign: "center", marginBottom: "20px", color: "#92400e", lineHeight: 1.2 }}>
              Activités exclues déjà définies
            </div>
            <div style={{ fontSize: "30px", color: "#4b5563", textAlign: "center", marginBottom: "30px", lineHeight: 1.5 }}>
              Vous avez déjà exclu des activités pour <strong>{moisLabel(MOIS_CLE)}</strong>.<br />
              Souhaitez-vous modifier ces exclusions ?
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button
                onClick={() => setShowExclusionsReminder(false)}
                style={{ ...S.btnCancel, padding: "20px 42px", fontSize: "28px", borderRadius: "14px" }}
              >
                Non, continuer
              </button>
              <button
                onClick={() => {
                  setShowExclusionsReminder(false);
                  setSetupDone(false);
                }}
                style={{ ...S.btnSave, padding: "20px 42px", fontSize: "28px", borderRadius: "14px", background: "#d97706" }}
              >
                Oui, modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== POPUP CONSIGNES DESIDERATAS ====== */}
      {showGuidelinesPopup && (
        <div style={S.overlay} onClick={() => setShowGuidelinesPopup(false)}>
          <div style={S.modalBoxLarge} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "58px", marginBottom: "16px", textAlign: "center", color: "#1d4ed8", fontWeight: 800 }}>
              Désidératas planning Port-Royal — Jours à compléter
            </div>
            <div style={{ fontSize: "30px", color: "#374151", lineHeight: 1.55, marginBottom: "28px" }}>
              <p style={{ margin: 0 }}>
                Merci de bien vouloir choisir les jours à compléter {guidelinesMoisPhrase}
              </p>
              <p style={{ margin: "16px 0 0", fontSize: "26px", color: "#6b7280" }}>
                Merci de renseigner uniquement les jours affichés pour ce planning.
              </p>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => setShowGuidelinesPopup(false)}
                style={{ ...S.btnSave, padding: "22px 46px", fontSize: "30px", borderRadius: "14px", background: "#2563eb" }}
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ====== STYLES ======
const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f0f4ff 0%, #f9fafb 40%, #ecfeff 100%)",
  },
  header: {
    background: "#1e293b",
    color: "#f8fafc",
    padding: "12px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    flexWrap: "wrap",
    gap: "8px",
  },
  title: { fontSize: "20px", fontWeight: 800, margin: 0, color: "#f8fafc" },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "16px",
    zoom: PAGE_UI_SCALE,
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap",
    gap: "12px", marginBottom: "12px", padding: "12px 16px",
    background: "#ffffff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  legend: {
    display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "8px",
    padding: "8px 16px", background: "#ffffff", borderRadius: "8px", fontSize: "12px", color: "#374151",
  },
  legendItem: { display: "flex", alignItems: "center", gap: "5px" },
  legendDot: { width: "14px", height: "14px", borderRadius: "3px", display: "inline-block" },
  stats: {
    display: "flex", flexWrap: "wrap", gap: "16px", marginBottom: "12px",
    padding: "6px 16px", fontSize: "13px", color: "#374151",
  },
  btnSave: {
    padding: "10px 24px", borderRadius: "8px", border: "none",
    background: "#059669", color: "#ffffff", fontSize: "15px", fontWeight: 700, cursor: "pointer",
  },
  btnBack: {
    padding: "10px 20px", borderRadius: "8px", border: "1px solid #111827",
    background: "#111827", color: "#ffffff", fontSize: "14px", fontWeight: 600, cursor: "pointer",
  },
  setupWrapper: {
    background: "#ffffff", borderRadius: "16px", padding: "24px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)", marginBottom: "16px",
  },
  setupHeader: {
    marginBottom: "20px", paddingBottom: "16px", borderBottom: "2px solid #e5e7eb",
  },
  setupGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "8px",
  },
  btnRetourToolbar: {
    padding: "8px 18px", borderRadius: "8px", border: "2px solid #1e293b",
    background: "#1e293b", color: "#ffffff", fontSize: "14px", fontWeight: 700, cursor: "pointer",
    whiteSpace: "nowrap",
  },

  // Calendrier
  calendarWrapper: {
    background: "#ffffff", borderRadius: "16px", padding: "16px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  },
  calendarHeader: {
    display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "4px",
  },
  calendarHeaderCell: {
    textAlign: "center", fontWeight: 700, fontSize: "14px", padding: "8px 0",
  },
  calendarRow: {
    display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "4px",
  },
  calendarCell: {
    minHeight: "100px", padding: "6px 8px", borderRadius: "10px",
    display: "flex", flexDirection: "column", transition: "all 0.15s",
    position: "relative",
  },
  calendarCellEmpty: {
    minHeight: "100px", background: "transparent",
  },

  // Popup
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999,
  },
  popup: {
    background: "#ffffff", borderRadius: "20px", padding: "36px 42px",
    maxWidth: "1100px", width: "96vw", maxHeight: "92vh", overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  popupHeader: {
    display: "flex", alignItems: "center", gap: "12px",
    marginBottom: "16px", paddingBottom: "12px", borderBottom: "2px solid #e5e7eb",
  },
  popupNavBtn: {
    padding: "8px 14px", borderRadius: "8px", border: "1px solid #d1d5db",
    background: "#f9fafb", cursor: "pointer", fontSize: "16px", fontWeight: 700,
  },
  popupCloseBtn: {
    padding: "6px 12px", borderRadius: "8px", border: "none",
    background: "#f3f4f6", cursor: "pointer", fontSize: "18px", color: "#6b7280",
    position: "absolute", top: "16px", right: "16px",
  },
  gardeSection: {
    background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: "10px",
    padding: "10px 14px", marginBottom: "16px",
  },
  statutGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: "8px",
  },
  statutBtn: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: "12px 8px", borderRadius: "12px", cursor: "pointer",
    transition: "all 0.2s", minHeight: "80px",
  },
  subSection: { marginBottom: "16px" },
  subAlert: {
    display: "flex", gap: "12px", alignItems: "flex-start",
    padding: "12px 16px", borderRadius: "10px", border: "1px solid", marginBottom: "10px",
  },
  inputField: {
    width: "100%", padding: "10px 14px", borderRadius: "8px",
    border: "2px solid #d1d5db", fontSize: "14px", outline: "none",
    boxSizing: "border-box",
  },
  activitesSection: {
    background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "12px",
    padding: "12px 14px", marginBottom: "16px",
  },
  activitesGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "6px",
  },
  miniBtn: {
    padding: "4px 10px", borderRadius: "6px", border: "none",
    background: "#d1fae5", color: "#065f46", fontSize: "11px", fontWeight: 700,
    cursor: "pointer",
  },
  popupFooter: {
    display: "flex", gap: "10px", justifyContent: "flex-end",
    paddingTop: "16px", borderTop: "2px solid #e5e7eb",
  },
  btnCancel: {
    padding: "10px 20px", borderRadius: "8px", border: "1px solid #d1d5db",
    background: "#f9fafb", color: "#374151", fontSize: "14px", fontWeight: 600, cursor: "pointer",
  },
  btnValidate: {
    padding: "10px 24px", borderRadius: "8px", border: "none",
    background: "#059669", color: "#ffffff", fontSize: "14px", fontWeight: 700, cursor: "pointer",
  },
  btnValidateNext: {
    padding: "10px 24px", borderRadius: "8px", border: "none",
    background: "#2563eb", color: "#ffffff", fontSize: "14px", fontWeight: 700, cursor: "pointer",
  },
  modalBox: {
    background: "#ffffff", borderRadius: "20px", padding: "48px 60px",
    maxWidth: "780px", width: "94vw", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  },
  modalBoxLarge: {
    background: "#ffffff",
    borderRadius: "24px",
    padding: "68px 76px",
    maxWidth: "1500px",
    width: "98vw",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  },
};

export default DesiderataPlanningPortRoyal;
